export type DeckListImageMode = 'list' | 'sprites'

export interface DeckListImageRow {
  id: string
  name: string
  count: number
}

export interface DeckListImageSection {
  title: string
  tone: 'monster' | 'spell' | 'trap' | 'extra'
  total: number
  rows: DeckListImageRow[]
}

export interface DeckListImageColors {
  surface: string
  onSurface: string
  variant: string
  primary: string
  monster: string
  spell: string
  trap: string
  placeholder: string
}

export interface DeckListImageInput {
  mode: DeckListImageMode
  name: string
  counts: string
  sections: DeckListImageSection[]
  colors: DeckListImageColors
  imageUrl: (id: string) => string
}

const CARD_RATIO = 86 / 59

async function loadCard(src: string): Promise<HTMLImageElement | null> {
  const img = new Image()
  img.src = src
  try {
    await img.decode()
    return img.naturalWidth > 0 ? img : null
  } catch {
    return null
  }
}

function toneColor(colors: DeckListImageColors, tone: DeckListImageSection['tone']): string {
  if (tone === 'monster') return colors.monster
  if (tone === 'spell') return colors.spell
  if (tone === 'trap') return colors.trap
  return colors.primary
}

function drawCover(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement | null,
  x: number,
  y: number,
  w: number,
  h: number,
  fill: string,
): void {
  ctx.save()
  ctx.beginPath()
  ctx.roundRect(x, y, w, h, 4)
  ctx.clip()
  ctx.fillStyle = fill
  ctx.fillRect(x, y, w, h)
  if (img) {
    const scale = Math.max(w / img.naturalWidth, h / img.naturalHeight)
    const sw = w / scale
    const sh = h / scale
    const sx = (img.naturalWidth - sw) / 2
    const sy = (img.naturalHeight - sh) / 2
    ctx.drawImage(img, sx, sy, sw, sh, x, y, w, h)
  }
  ctx.restore()
}

function wrapLine(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
): string {
  if (ctx.measureText(text).width <= maxWidth) return text
  let cut = text
  while (cut.length > 1 && ctx.measureText(`${cut}…`).width > maxWidth) {
    cut = cut.slice(0, -1)
  }
  return `${cut}…`
}

export async function renderDeckListPng(input: DeckListImageInput): Promise<Blob> {
  await document.fonts.ready
  const ids = new Set<string>()
  for (const section of input.sections) {
    for (const row of section.rows) ids.add(row.id)
  }
  const images = new Map<string, HTMLImageElement | null>()
  await Promise.all(
    [...ids].map(async (id) => {
      images.set(id, await loadCard(input.imageUrl(id)))
    }),
  )

  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('canvas')

  if (input.mode === 'sprites') {
    paintSprites(canvas, ctx, input, images)
  } else {
    paintNames(canvas, ctx, input, images)
  }

  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, 'image/png')
  })
  if (!blob) throw new Error('png')
  return blob
}

function paintNames(
  canvas: HTMLCanvasElement,
  ctx: CanvasRenderingContext2D,
  input: DeckListImageInput,
  images: Map<string, HTMLImageElement | null>,
): void {
  const width = 1000
  const pad = 36
  const colGap = 24
  const cols = 2
  const colW = (width - pad * 2 - colGap) / cols
  const thumbW = 52
  const thumbH = Math.round(thumbW * CARD_RATIO)
  const rowH = thumbH + 10
  let height = pad + 40 + 32
  for (const section of input.sections) {
    height += 30
    height += Math.ceil(section.rows.length / cols) * rowH
    height += 14
  }
  height += pad

  canvas.width = width
  canvas.height = height
  ctx.fillStyle = input.colors.surface
  ctx.fillRect(0, 0, width, height)
  ctx.textBaseline = 'middle'

  let y = pad
  ctx.fillStyle = input.colors.onSurface
  ctx.font = '400 28px Roboto, system-ui, sans-serif'
  ctx.textAlign = 'center'
  ctx.fillText(input.name, width / 2, y + 16)
  y += 40
  ctx.fillStyle = input.colors.variant
  ctx.font = '500 16px Roboto, system-ui, sans-serif'
  ctx.fillText(input.counts, width / 2, y + 10)
  y += 32
  ctx.textAlign = 'left'

  for (const section of input.sections) {
    ctx.font = '700 14px Roboto, system-ui, sans-serif'
    ctx.fillStyle = toneColor(input.colors, section.tone)
    const label = `${section.total}  ${section.title.toUpperCase()}`
    ctx.fillText(label, pad, y + 10)
    y += 30
    section.rows.forEach((row, index) => {
      const col = index % cols
      const line = Math.floor(index / cols)
      const x = pad + col * (colW + colGap)
      const rowY = y + line * rowH
      drawCover(
        ctx,
        images.get(row.id) ?? null,
        x,
        rowY,
        thumbW,
        thumbH,
        input.colors.placeholder,
      )
      const textX = x + thumbW + 10
      const textW = colW - thumbW - 10
      ctx.fillStyle = input.colors.primary
      ctx.font = '700 18px Roboto, system-ui, sans-serif'
      const qty = `${row.count}×`
      ctx.fillText(qty, textX, rowY + thumbH / 2)
      const qtyW = ctx.measureText(qty).width
      ctx.fillStyle = input.colors.onSurface
      ctx.font = '400 18px Roboto, system-ui, sans-serif'
      ctx.fillText(
        wrapLine(ctx, row.name, textW - qtyW - 8),
        textX + qtyW + 8,
        rowY + thumbH / 2,
      )
    })
    y += Math.ceil(section.rows.length / cols) * rowH + 14
  }
}

function paintSprites(
  canvas: HTMLCanvasElement,
  ctx: CanvasRenderingContext2D,
  input: DeckListImageInput,
  images: Map<string, HTMLImageElement | null>,
): void {
  const width = 1080
  const pad = 28
  const gap = 10
  const cols = 6
  const cardW = (width - pad * 2 - gap * (cols - 1)) / cols
  const cardH = Math.round(cardW * CARD_RATIO)
  let height = pad + 40 + 32
  for (const section of input.sections) {
    const copies = section.rows.reduce((sum, row) => sum + row.count, 0)
    height += 28
    height += Math.ceil(copies / cols) * (cardH + gap)
    height += 8
  }
  height += pad

  canvas.width = width
  canvas.height = height
  ctx.fillStyle = input.colors.surface
  ctx.fillRect(0, 0, width, height)
  ctx.textBaseline = 'middle'
  ctx.textAlign = 'center'

  let y = pad
  ctx.fillStyle = input.colors.onSurface
  ctx.font = '400 28px Roboto, system-ui, sans-serif'
  ctx.fillText(input.name, width / 2, y + 16)
  y += 40
  ctx.fillStyle = input.colors.variant
  ctx.font = '500 16px Roboto, system-ui, sans-serif'
  ctx.fillText(input.counts, width / 2, y + 10)
  y += 32
  ctx.textAlign = 'left'

  for (const section of input.sections) {
    ctx.font = '700 14px Roboto, system-ui, sans-serif'
    ctx.fillStyle = toneColor(input.colors, section.tone)
    ctx.fillText(`${section.total}  ${section.title.toUpperCase()}`, pad, y + 10)
    y += 28
    const ids: string[] = []
    for (const row of section.rows) {
      for (let i = 0; i < row.count; i++) ids.push(row.id)
    }
    ids.forEach((id, index) => {
      const col = index % cols
      const line = Math.floor(index / cols)
      const x = pad + col * (cardW + gap)
      const cardY = y + line * (cardH + gap)
      drawCover(ctx, images.get(id) ?? null, x, cardY, cardW, cardH, input.colors.placeholder)
    })
    y += Math.ceil(ids.length / cols) * (cardH + gap) + 8
  }
}
